import { useCallback, useEffect, useRef, useState } from "react";
import {
  Download,
  FileText,
  Upload,
  Trash2,
  Presentation,
  ChevronRight,
  ChevronUp,
  Search,
  FolderOpen,
  Folder,
  Plus,
  X,
  Eye,
  CheckCircle2,
  Minus,
  Minimize2,
  Maximize2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiCall } from "@/api/apiCall";
import { BASE_URL } from "@/constants/constant";
import { getAccessToken } from "@/utils/authSession";
import { openPdfByRange } from "@/lib/pdfRangeTransport";
import ReactSelect from "@/components/common/ReactSelect";
import { PdfViewer } from "@/components/child/PdfViewer";
import { PptViewer } from "@/components/child/PptViewer";
import { formatDateTime } from "@/lib/formatDateTime";
import { usePermissions } from "@/context/PermissionContext";
import LoadingComponent from "@/components/common/LoadingComponent";
import FinalizeRequestsPanel from "@/components/BoardPack/FinalizeRequestsPanel";

// ─── Constants ───────────────────────────────────────────────
const CHUNK_SIZE = 512 * 1024;
// pdf.js fetches only the byte ranges it needs for the page on screen
const PDF_OPTIONS = { rangeChunkSize: 512 * 1024, disableAutoFetch: true };

// ─── Stream Report (full blob) ────────────────────────────────
const fetchReportStream = async (id, type) => {
  const blob = await apiCall(
    "get",
    "/reports/stream",
    { id, type },
    null,
    false,
    false,
    false,
    "blob"
  );
  return blob;
};

// ─── Chunked Upload ───────────────────────────────────────────
const uploadFileInChunks = async (file, contract, year, month, onProgress) => {
  const type = file.name.toLowerCase().endsWith(".pdf") ? "pdf" : "pptx";
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const upload_id = `${contract.replace(/[^a-zA-Z0-9]/g, "_")}_${year}_${month}_${Date.now()}`;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const formData = new FormData();
    formData.append("contract", contract);
    formData.append("year", year);
    formData.append("month", month);
    formData.append("type", type);
    formData.append("chunk_index", String(i));
    formData.append("total_chunks", String(totalChunks));
    formData.append("chunk", chunk, file.name);
    formData.append("upload_id", upload_id);

    const res = await apiCall(
      "post",
      "/report/submit-report",
      formData,
      null,
      true,
      i === totalChunks - 1
    );

    if (!res?.success && res?.success !== undefined) {
      throw new Error(res?.message || "Upload failed");
    }

    onProgress(Math.round(((i + 1) / totalChunks) * 100));
  }
};

// ─── Helpers ─────────────────────────────────────────────────
const triggerAnchorDownload = (href, filename) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// ─── Last finalized helpers ───────────────────────────────────
const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const monthToIndex = (m) => MONTH_INDEX[String(m ?? "").slice(0, 3).toLowerCase()] ?? 0;

// Higher score = later period (year first, then month)
const periodScore = (r) => (Number(r.year) || 0) * 12 + monthToIndex(r.month);

// Strict check so values like "0" / "false" are not treated as finalized
const isFinalizedFlag = (v) => v === true || v === 1 || v === "1" || v === "true";

// Latest finalized report of a contract (by period, tie-break by upload time)
const getLastFinalized = (files) => {
  const finalized = files.filter((f) => isFinalizedFlag(f.is_finalized));
  if (finalized.length === 0) return null;

  return finalized.reduce((best, f) => {
    const diff = periodScore(f) - periodScore(best);
    if (diff !== 0) return diff > 0 ? f : best;
    return new Date(f.created_datetime) > new Date(best.created_datetime) ? f : best;
  });
};

// ─── FileTypeBadge ────────────────────────────────────────────
const FileTypeBadge = ({ hasPdf, hasPpt }) => {
  if (hasPdf && hasPpt) {
    return (
      <div className="flex flex-col gap-1">
        <div className="w-10 h-[18px] rounded flex items-center justify-center text-[10px] font-bold tracking-wide bg-red-50 text-red-700">
          PDF
        </div>
        <div className="w-10 h-[18px] rounded flex items-center justify-center text-[10px] font-bold tracking-wide bg-orange-50 text-orange-700">
          PPT
        </div>
      </div>
    );
  }
  if (hasPdf) {
    return (
      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-[11px] font-bold tracking-wide flex-shrink-0 bg-red-50 text-red-700">
        PDF
      </div>
    );
  }
  return (
    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-[11px] font-bold tracking-wide flex-shrink-0 bg-orange-50 text-orange-700">
      PPT
    </div>
  );
};

// ─── TypeCard ─────────────────────────────────────────────────
const TypeCard = ({ type, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(type)}
    className={`border rounded-lg p-4 text-center cursor-pointer transition-all flex flex-col items-center gap-2 ${
      selected
        ? "border-blue-400 bg-blue-50"
        : "border-border hover:border-muted-foreground/40 hover:bg-muted/40"
    }`}
  >
    {type === "pdf" ? (
      <FileText className={`w-7 h-7 ${selected ? "text-blue-600" : "text-muted-foreground"}`} />
    ) : (
      <Presentation className={`w-7 h-7 ${selected ? "text-blue-600" : "text-muted-foreground"}`} />
    )}
    <div>
      <p className="text-sm font-medium text-foreground">{type === "pdf" ? "PDF" : "PowerPoint"}</p>
      <p className="text-[11px] text-muted-foreground">
        {type === "pdf" ? ".pdf format" : ".pptx format"}
      </p>
    </div>
  </button>
);

// ─── DocViewerModal ───────────────────────────────────────────
const DocViewerModal = ({ open, onClose, doc, title, displayType }) => {
  const isPdfStream = doc?.fileType === "pdf";

  // PDFs open with Range requests: pdf.js reads only the page on screen
  // instead of downloading the whole file first (see lib/pdfRangeTransport)
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    if (!open || !isPdfStream || !doc?.id) return;
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);
    setPdfFile(null);

    openPdfByRange(`${BASE_URL}/reports/stream?id=${encodeURIComponent(doc.id)}&type=pdf`, {
      Authorization: `Bearer ${getAccessToken()}`,
    })
      .then((transport) => !cancelled && setPdfFile(transport))
      .catch((err) => !cancelled && setPdfError(err?.message || "Failed to load file"))
      .finally(() => !cancelled && setPdfLoading(false));

    return () => {
      cancelled = true;
    };
  }, [open, isPdfStream, doc?.id]);

  const [pptUrl, setPptUrl] = useState(null);
  const [pptLoading, setPptLoading] = useState(false);
  const [pptError, setPptError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (!open || !doc || isPdfStream) return;
    let objectUrl = null;
    setPptLoading(true);
    setPptError(null);
    setPptUrl(null);

    fetchReportStream(doc.id, "pptx")
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setPptUrl(objectUrl);
      })
      .catch((err) => setPptError(err?.message || err || "Failed to load file"))
      .finally(() => setPptLoading(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, doc?.id, isPdfStream]);

  if (!doc) return null;

  const loading = isPdfStream ? pdfLoading : pptLoading;
  const error = isPdfStream ? pdfError : pptError;
  const url = isPdfStream ? pdfFile : pptUrl;
  const labelIsPdf = (displayType ?? doc.fileType) === "pdf";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        aria-describedby={undefined}
        className={`flex flex-col p-0 gap-0 transition-all duration-200 ${
          isMaximized
            ? "!max-w-none !w-screen !h-screen !top-0 !left-0 !translate-x-0 !translate-y-0 !rounded-none"
            : "!w-[95vw] !max-w-7xl lg:!min-w-5xl h-[90vh]"
        }`}
      >
        <DialogHeader className="px-3 sm:px-4 py-3 border-b shrink-0">
          <div className="flex items-center justify-between gap-3 pr-8">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide shrink-0 ${
                  labelIsPdf ? "bg-red-50 text-red-700" : "bg-orange-50 text-orange-700"
                }`}
              >
                {labelIsPdf ? (
                  <FileText className="w-3 h-3" />
                ) : (
                  <Presentation className="w-3 h-3" />
                )}
                {labelIsPdf ? "PDF" : "PPT"}
              </span>
              <DialogTitle className="text-sm font-medium text-foreground truncate">
                {title}
              </DialogTitle>
            </div>

            <button
              type="button"
              onClick={() => setIsMaximized((prev) => !prev)}
              title={isMaximized ? "Minimize" : "Maximize"}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground shrink-0"
            >
              {isMaximized ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex items-center justify-center">
          {loading && <LoadingComponent />}
          {error && !loading && (
            <div className="text-sm text-red-500 text-center px-4">
              Failed to load file: {error}
            </div>
          )}
          {url && !loading && (
            <div className="w-full h-full">
              {isPdfStream ? (
                <PdfViewer url={url} options={PDF_OPTIONS} showNavigation />
              ) : (
                <PptViewer url={url} />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Module-level cache for contract options ──────────────────
// Ensures /boardpack/contracts is fetched only once per app session,
// regardless of how many times ImportModal mounts.
let _contractOptionsCache = null;
let _contractOptionsPromise = null;

export const clearContractOptionsCache = () => {
  _contractOptionsCache = null;
  _contractOptionsPromise = null;
};

const loadContractOptions = () => {
  if (_contractOptionsCache) return Promise.resolve(_contractOptionsCache);
  if (_contractOptionsPromise) return _contractOptionsPromise;

  _contractOptionsPromise = (async () => {
    try {
      const response = await apiCall("get", "/boardpack/contracts");
      if (response?.data) {
        const contracts = [
          { name: "ALL COMPANIES", description: "ALL COMPANIES" },
          { name: "Jeddah Airport Contract", description: "Jeddah Airport Contract" },
          { name: "Sharjah Airport Parking", description: "Sharjah Airport Parking" },
          { name: "Airport Jordan", description: "Airport Jordan" },
          { name: "Lovers Mkt Off Str-dam", description: "Lovers Mkt Off Str-dam" },
          { name: "Al Batha Contract", description: "Al Batha Contract" },
          { name: "Dammam Airport Contract", description: "Dammam Airport Contract" },
          { name: "TOTAL BILAL PROJECT", description: "TOTAL BILAL PROJECT" },
          { name: "Bab Al-Ree Contract", description: "Bab Al-Ree Contract" },
          { name: "Gazzaz Contract", description: "Gazzaz Contract" },
          { name: "Jed Watch Mkt-parking", description: "Jed Watch Mkt-parking" },
          { name: "TOTAL DUBAI", description: "TOTAL DUBAI" },
          { name: "Haram Contract", description: "Haram Contract" },
          { name: "Taif Contract", description: "Taif Contract" },
          { name: "Khamis Contract", description: "Khamis Contract" },
          { name: "TOTAL JUBAIL", description: "TOTAL JUBAIL" },
          { name: "Hassa Contract", description: "Hassa Contract" },
          { name: "Digital City Contract", description: "Digital City Contract" },
          { name: "Baha Contract", description: "Baha Contract" },
          { name: "Dammam Causeway", description: "Dammam Causeway" },
          { name: "Baljurashi Contract", description: "Baljurashi Contract" },
          { name: "Makkah Contract", description: "Makkah Contract" },
          { name: "Jeddah Contract", description: "Jeddah Contract" },
          { name: "MOC Contract", description: "MOC Contract" },
          { name: "Modern City Contract", description: "Modern City Contract" },
          { name: "CAPITAL PARKING CO. LTD", description: "CAPITAL PARKING CO. LTD" },
        ];

        _contractOptionsCache = {
          contracts: contracts.map((item) => ({
            label: item.description,
            value: item.name,
          })),
          years: response.data.years.map((item) => ({
            label: item.description,
            value: item.name,
          })),
          months: response.data.months.map((item) => ({
            label: item.description,
            value: item.name,
          })),
        };
      } else {
        _contractOptionsCache = { contracts: [], years: [], months: [] };
      }
      return _contractOptionsCache;
    } catch (err) {
      console.error("Failed to fetch contract options:", err);
      _contractOptionsCache = { contracts: [], years: [], months: [] };
      return _contractOptionsCache;
    } finally {
      _contractOptionsPromise = null;
    }
  })();

  return _contractOptionsPromise;
};

// ─── useContractOptions Hook (uses shared cache) ──────────────
const useContractOptions = () => {
  const [contracts, setContracts] = useState(_contractOptionsCache?.contracts ?? []);
  const [years, setYears] = useState(_contractOptionsCache?.years ?? []);
  const [months, setMonths] = useState(_contractOptionsCache?.months ?? []);
  const [loadingOptions, setLoadingOptions] = useState(!_contractOptionsCache);
  const hasFetched = useRef(false);

  useEffect(() => {
    // Cache exists → hydrate from it, no network call
    if (_contractOptionsCache) {
      if (hasFetched.current) return;
      hasFetched.current = true;
      setContracts(_contractOptionsCache.contracts);
      setYears(_contractOptionsCache.years);
      setMonths(_contractOptionsCache.months);
      setLoadingOptions(false);
      return;
    }

    if (hasFetched.current) return;
    hasFetched.current = true;

    let cancelled = false;
    setLoadingOptions(true);

    loadContractOptions().then((data) => {
      if (cancelled) return;
      setContracts(data.contracts);
      setYears(data.years);
      setMonths(data.months);
      setLoadingOptions(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { contracts, years, months, loadingOptions };
};

// ─── ImportModal ──────────────────────────────────────────────
const ImportModal = ({ open, onClose, contractName, onUploadSuccess }) => {
  const { contracts, years, months, loadingOptions } = useContractOptions();
  const [selectedType, setSelectedType] = useState("pdf");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [contractOverride, setContractOverride] = useState(null);
  const [yearOverride, setYearOverride] = useState(null);
  const [monthOverride, setMonthOverride] = useState(null);
  const inputRef = useRef(null);

  const _currentYear = new Date().getFullYear().toString();
  const _currentMonthShort = new Date().toLocaleString("en-US", { month: "short" });
  const defaultContract =
    !loadingOptions && contractName
      ? (contracts.find((c) => c.value === contractName)?.value ?? null)
      : null;
  const defaultYear =
    !loadingOptions && years.length > 0
      ? (years.find((y) => y.value === _currentYear)?.value ?? years[0]?.value ?? null)
      : null;
  const defaultMonth =
    !loadingOptions && months.length > 0
      ? (months.find((m) => m.value === _currentMonthShort)?.value ?? months[0]?.value ?? null)
      : null;

  const contract = contractOverride ?? defaultContract;
  const year = yearOverride ?? defaultYear;
  const month = monthOverride ?? defaultMonth;

  const reset = () => {
    setSelectedFile(null);
    setSelectedType("pdf");
    setProgress(0);
    setUploading(false);
    setContractOverride(null);
    setYearOverride(null);
    setMonthOverride(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    if (!uploading) {
      reset();
      onClose();
    }
  };

  const processFile = (file) => {
    const name = file.name.toLowerCase();
    const isPdf = name.endsWith(".pdf");
    const isPpt = name.endsWith(".pptx") || name.endsWith(".ppt");
    if (selectedType === "pdf" && !isPdf) return alert("Please select a .pdf file");
    if (selectedType === "ppt" && !isPpt) return alert("Please select a .pptx file");
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);
  };

  const handleConfirm = async () => {
    if (!selectedFile || uploading || !contract || !year || !month) return;
    setUploading(true);
    setProgress(0);
    try {
      await uploadFileInChunks(selectedFile, contract, year, month, setProgress);
      reset();
      onClose();
      setTimeout(() => onUploadSuccess(), 1500);
    } catch (err) {
      console.error("Upload error:", err);
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import board pack</DialogTitle>
        </DialogHeader>

        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Contract</label>
          <ReactSelect
            options={contracts}
            value={contract ? contracts.find((c) => c.value === contract) : null}
            onChange={(option) => setContractOverride(option?.value ?? null)}
            placeholder="Select Contract"
            isDisabled={uploading || loadingOptions}
            width="100%"
            className="w-full"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Year</label>
            <ReactSelect
              options={years}
              value={year ? years.find((y) => y.value === year) : null}
              onChange={(option) => setYearOverride(option?.value ?? null)}
              placeholder="Year"
              isDisabled={uploading || loadingOptions}
              width="100%"
              className="w-full"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Month</label>
            <ReactSelect
              options={months}
              value={month ? months.find((m) => m.value === month) : null}
              onChange={(option) => setMonthOverride(option?.value ?? null)}
              placeholder="Month"
              isDisabled={uploading || loadingOptions}
              width="100%"
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <TypeCard
            type="pdf"
            selected={selectedType === "pdf"}
            onSelect={(t) => {
              if (!uploading) {
                setSelectedType(t);
                setSelectedFile(null);
              }
            }}
          />
          <TypeCard
            type="ppt"
            selected={selectedType === "ppt"}
            onSelect={(t) => {
              if (!uploading) {
                setSelectedType(t);
                setSelectedFile(null);
              }
            }}
          />
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => {
            if (!uploading) inputRef.current?.click();
          }}
          className={`relative border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-all ${
            uploading
              ? "opacity-50 cursor-not-allowed"
              : isDragging
                ? "border-blue-400 bg-blue-50 cursor-pointer"
                : "border-border hover:border-muted-foreground/40 hover:bg-muted/30 cursor-pointer"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={selectedType === "pdf" ? ".pdf" : ".pptx,.ppt"}
            className="hidden"
            onChange={(e) => e.target.files[0] && processFile(e.target.files[0])}
          />
          <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            <span className="text-blue-600 font-medium">Choose file</span> or drag & drop
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Accepts {selectedType === "pdf" ? ".pdf" : ".pptx"} files
          </p>
        </div>

        {selectedFile && !uploading && (
          <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm text-foreground">
            <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[9px] font-bold">✓</span>
            </div>
            <span className="truncate flex-1">{selectedFile.name}</span>
            <button
              type="button"
              onClick={() => setSelectedFile(null)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {uploading && (
          <div className="space-y-1.5">
            <div className="flex justify-between gap-2 text-xs text-muted-foreground">
              <span className="truncate">Uploading {selectedFile?.name}</span>
              <span className="flex-shrink-0">{progress}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <Button
          onClick={handleConfirm}
          disabled={!selectedFile || uploading || !contract || !year || !month}
          className="w-full"
        >
          {uploading ? `Uploading… ${progress}%` : "Upload file"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// ─── Breadcrumb ───────────────────────────────────────────────
const Breadcrumb = ({ items }) => (
  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
    {items.map((item, index) => {
      const isLast = index === items.length - 1;
      return (
        <div key={index} className="flex items-center gap-1.5 min-w-0">
          {isLast ? (
            <span className="text-sm font-medium text-foreground truncate max-w-[200px] sm:max-w-[220px]">
              {item.label}
            </span>
          ) : (
            <button
              type="button"
              onClick={item.onClick}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors truncate max-w-[110px] sm:max-w-[160px]"
            >
              {item.label}
            </button>
          )}
          {!isLast && (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />
          )}
        </div>
      );
    })}
  </div>
);

// ─── Finalize Confirm Dialog ──────────────────────────────────
const FinalizeConfirmDialog = ({ open, onCancel, onConfirm }) => (
  <Dialog open={open} onOpenChange={onCancel}>
    <DialogContent className="w-[95vw] sm:max-w-xs">
      <DialogHeader>
        <DialogTitle className="text-center">Mark as Finalized?</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-muted-foreground text-center mt-1">
        Are you sure you want to finalize this board pack? This action will mark it as finalized.
      </p>
      <div className="flex gap-3 mt-4">
        <Button variant="outline" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={onConfirm}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
          Confirm
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// ─── Delete Confirm Dialog ────────────────────────────────────
const DeleteConfirmDialog = ({ open, onCancel, onConfirm }) => (
  <Dialog open={open} onOpenChange={onCancel}>
    <DialogContent className="w-[95vw] sm:max-w-xs">
      <DialogHeader>
        <DialogTitle className="text-center">Delete Board Pack?</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-muted-foreground text-center mt-1">
        This action cannot be undone. The file will be permanently removed.
      </p>
      <div className="flex gap-3 mt-4">
        <Button variant="outline" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="flex-1 bg-destructive hover:bg-destructive/90 text-white"
          onClick={onConfirm}
        >
          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
          Delete
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// ─── LastFinalizedInfo ────────────────────────────────────────
const LastFinalizedInfo = ({ report }) => {
  if (!report) {
    return <p className="text-xs text-muted-foreground/70 mt-1">No finalized board pack yet</p>;
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground min-w-0">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">
        <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
        Last finalized: {report.month} {report.year}
      </span>
      {report.finalized_by && <span className="truncate max-w-full">by {report.finalized_by}</span>}
    </div>
  );
};

// ─── Contract List View ───────────────────────────────────────
const ContractListView = ({
  reports,
  loading,
  onSelectContract,
  searchQuery,
  setSearchQuery,
  onImportSuccess,
}) => {
  const [importOpen, setImportOpen] = useState(false);

  const contractMap = {};
  reports.forEach((r) => {
    if (!contractMap[r.contract]) contractMap[r.contract] = { name: r.contract, files: [] };
    contractMap[r.contract].files.push(r);
  });

  const filtered = Object.values(contractMap)
    .filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({
      name: c.name,
      count: c.files.length,
      lastFinalized: getLastFinalized(c.files),
    }));

  return (
    <div className="w-full max-w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6 pb-4 border-b">
        <div className="min-w-0">
          <h1 className="text-base sm:text-lg font-medium text-foreground">
            Finalized BoardPacks
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            Select a contract to view its boardpacks
          </p>
        </div>
        <Button onClick={() => setImportOpen(true)} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Import file
        </Button>
      </div>

      {/* <FinalizeRequestsPanel onJobFinished={onImportSuccess} /> */}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search contracts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        />
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-[72px] rounded-xl bg-muted/50 animate-pulse" />
          ))}
        </div>
      )}

      {/* List */}
      {!loading && (
        <div className="flex flex-col gap-2">
          {filtered.map((contract) => (
            <button
              key={contract.name}
              type="button"
              onClick={() => onSelectContract(contract.name)}
              className="w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-3.5 bg-background border border-border rounded-xl hover:bg-muted/40 hover:border-muted-foreground/30 transition-all text-left group"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Folder className="w-4 h-4 text-blue-600 group-hover:hidden" />
                <FolderOpen className="w-4 h-4 text-blue-600 hidden group-hover:block" />
              </div>

              {/* Name + last finalized (wraps on small screens instead of overflowing) */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{contract.name}</p>
                <LastFinalizedInfo report={contract.lastFinalized} />
              </div>

              <div className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2">
                <span className="text-xs font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {contract.count} file{contract.count !== 1 ? "s" : ""}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {searchQuery ? "No contracts match your search" : "No board packs found"}
            </div>
          )}
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onUploadSuccess={() => {
          setImportOpen(false);
          onImportSuccess?.();
        }}
      />
    </div>
  );
};

// ─── Year List View ───────────────────────────────────────────
const YearListView = ({ contractName, reports, onSelectYear, onBackToRoot, onImportSuccess }) => {
  const [importOpen, setImportOpen] = useState(false);

  const files = reports.filter((r) => r.contract === contractName);
  const yearMap = {};
  files.forEach((f) => {
    const y = f.year || "Unknown";
    if (!yearMap[y]) yearMap[y] = 0;
    yearMap[y] += 1;
  });
  const sortedYears = Object.keys(yearMap).sort((a, b) => {
    if (a === "Unknown") return 1;
    if (b === "Unknown") return -1;
    return Number(b) - Number(a);
  });

  return (
    <div className="w-full max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6 pb-4 border-b">
        <Breadcrumb
          items={[{ label: "Contracts", onClick: onBackToRoot }, { label: contractName }]}
        />
        <Button onClick={() => setImportOpen(true)} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Import file
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-5 sm:mb-6 px-3 sm:px-4 py-3 bg-muted/40 rounded-xl border border-border">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <FolderOpen className="w-4 h-4 text-blue-600" />
        </div>
        <p className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
          {contractName}
        </p>
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {sortedYears.length} year{sortedYears.length !== 1 ? "s" : ""}
        </span>
      </div>

      {sortedYears.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[260px] gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center border">
            <Folder className="w-6 h-6 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-medium text-foreground">No files yet</h3>
          <p className="text-sm text-muted-foreground max-w-[240px] leading-relaxed">
            Import a PDF or PowerPoint board pack for this contract
          </p>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import file
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {sortedYears.map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => onSelectYear(year)}
              className="w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-3.5 bg-background border border-border rounded-xl hover:bg-muted/40 hover:border-muted-foreground/30 transition-all text-left group"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Folder className="w-4 h-4 text-blue-600 group-hover:hidden" />
                <FolderOpen className="w-4 h-4 text-blue-600 hidden group-hover:block" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{year}</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2">
                <span className="text-xs font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {yearMap[year]} file{yearMap[year] !== 1 ? "s" : ""}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        contractName={contractName}
        onUploadSuccess={() => {
          setImportOpen(false);
          onImportSuccess();
        }}
      />
    </div>
  );
};

// ─── Files List View ──────────────────────────────────────────
const FilesListView = ({
  contractName,
  year,
  reports,
  onBackToYears,
  onBackToRoot,
  onImportSuccess,
  onDeleteClick,
}) => {
  const [importOpen, setImportOpen] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [localFinalized, setLocalFinalized] = useState({});
  const [pendingFinalize, setPendingFinalize] = useState(null);
  const [downloadDialog, setDownloadDialog] = useState(null);
  const { hasPermission } = usePermissions();

  const files = reports.filter(
    (r) => r.contract === contractName && (r.year || "Unknown") === year
  );

  const handleFinalizeToggle = async (f, newValue) => {
    if (togglingId) return;
    setTogglingId(f.id);

    try {
      const response = await apiCall("post", `/reports/mark-as-finalize`, { id: f.id });

      if (response?.success === true) {
        setLocalFinalized((prev) => ({ ...prev, [f.id]: newValue }));
        onImportSuccess();
      } else {
        console.error("Failed to update:", response?.message);
      }
    } catch (err) {
      console.error("Toggle failed:", err);
    } finally {
      setTogglingId(null);
    }
  };

  const openViewer = (requestedType, f) => {
    const hasPdf = !!f.pdf_url;
    const streamType = hasPdf ? "pdf" : "pptx";
    setViewer({
      doc: { id: f.id, fileType: streamType },
      title: `${f?.contract} ${f?.month} ${f?.year}`,
      displayType: requestedType,
    });
  };

  const handleDownload = async (id, type, name) => {
    setDownloadDialog({ id, type, name, downloading: true, minimized: false });
    try {
      const blob = await fetchReportStream(id, type);
      const url = URL.createObjectURL(blob);
      triggerAnchorDownload(url, `${name}.${type}`);
      URL.revokeObjectURL(url);
      setDownloadDialog(null);
    } catch {
      setDownloadDialog(null);
    }
  };

  return (
    <div className="w-full max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6 pb-4 border-b">
        <Breadcrumb
          items={[
            { label: "Contracts", onClick: onBackToRoot },
            { label: contractName, onClick: onBackToYears },
            { label: year },
          ]}
        />
        <Button onClick={() => setImportOpen(true)} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Import file
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-5 sm:mb-6 px-3 sm:px-4 py-3 bg-muted/40 rounded-xl border border-border">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <FolderOpen className="w-4 h-4 text-blue-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{contractName}</p>
          <p className="text-xs text-muted-foreground">{year}</p>
        </div>
        <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
          {files.length} board pack{files.length !== 1 ? "s" : ""}
        </span>
      </div>

      {files.length === 0 && (
        <div className="flex flex-col items-center justify-center min-h-[260px] gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center border">
            <FileText className="w-6 h-6 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-medium text-foreground">No files yet</h3>
          <p className="text-sm text-muted-foreground max-w-[240px] leading-relaxed">
            Import a PDF or PowerPoint board pack for this contract
          </p>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import file
          </Button>
        </div>
      )}

      {files.length > 0 && (
        <div className="flex flex-col gap-3">
          {files.map((f) => {
            const hasPdf = !!f.pdf_url;
            const hasPpt = !!f.pptx_url;
            const isFinalized = localFinalized[f.id] ?? f.is_finalized;

            return (
              <div
                key={f.id}
                className="bg-background border rounded-xl px-3 sm:px-4 py-3 sm:py-4 flex flex-col lg:flex-row lg:items-center gap-3"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-10 flex items-center justify-center">
                    <FileTypeBadge hasPdf={hasPdf} hasPpt={hasPpt} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-foreground truncate">
                        {f?.contract} - {f.month} {f.year}
                      </p>
                      {isFinalized && hasPermission("mark_finalized_boarpack") && (
                        <span className="lg:hidden flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-green-50 text-green-700 border-green-200">
                          <CheckCircle2 className="w-3 h-3" />
                          Finalized
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDateTime(f.created_datetime)}
                    </p>
                    {f?.finalized_by && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Finalized by{" "}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          {f.finalized_by}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:ml-auto lg:flex-shrink-0">
                  {hasPermission("mark_finalized_boarpack") &&
                    (isFinalized ? (
                      <span className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 bg-green-50 text-green-700 border-green-200">
                        Board Pack Finalized
                      </span>
                    ) : (
                      <button
                        disabled={togglingId === f.id}
                        onClick={() => setPendingFinalize({ f, newValue: !isFinalized })}
                        className={`self-start lg:self-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                          isFinalized
                            ? "bg-green-50 text-green-700 border-green-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                            : "bg-background text-muted-foreground border-border hover:bg-green-50 hover:text-green-700 hover:border-green-200"
                        }`}
                      >
                        {togglingId === f.id ? (
                          <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin inline-block" />
                        ) : (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        )}
                        {isFinalized ? "Board Pack Finalized" : "Mark as Finalized"}
                      </button>
                    ))}

                  {/* Action buttons: one equal-width row on mobile */}
                  <div className="flex items-center gap-2">
                  {/* View — dropdown when both, direct when single */}
                  {hasPermission("view_finalized_boarpacks") && (hasPdf || hasPpt) && (
                    <>
                      {hasPdf && hasPpt ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 lg:flex-none text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Eye className="w-3.5 h-3.5 mr-1.5" />
                              View
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openViewer("pdf", f)}>
                              <FileText className="w-3.5 h-3.5 mr-2" />
                              View PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openViewer("pptx", f)}>
                              <Presentation className="w-3.5 h-3.5 mr-2" />
                              View PPT
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 lg:flex-none text-blue-600 border-blue-200 hover:bg-blue-50"
                          onClick={() => openViewer(hasPdf ? "pdf" : "pptx", f)}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1.5" />
                          View
                        </Button>
                      )}
                    </>
                  )}

                  {/* Download */}
                  {hasPermission("download_finalized_boarpacks") && (
                    <>
                      {hasPdf && !hasPpt ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 lg:flex-none text-green-600 border-green-200 hover:bg-green-50"
                          disabled={!!downloadDialog?.downloading}
                          onClick={() =>
                            handleDownload(f.id, "pdf", `${f.contract}_${f.month}_${f.year}`)
                          }
                        >
                          <Download className="w-3.5 h-3.5 mr-1.5" />
                          Download
                        </Button>
                      ) : hasPpt && !hasPdf ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 lg:flex-none text-green-600 border-green-200 hover:bg-green-50"
                          disabled={!!downloadDialog?.downloading}
                          onClick={() =>
                            handleDownload(f.id, "pptx", `${f.contract}_${f.month}_${f.year}`)
                          }
                        >
                          <Download className="w-3.5 h-3.5 mr-1.5" />
                          Download
                        </Button>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 lg:flex-none text-green-600 border-green-200 hover:bg-green-50"
                              disabled={!!downloadDialog?.downloading}
                            >
                              <Download className="w-3.5 h-3.5 mr-1.5" />
                              Download
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {hasPdf && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDownload(
                                    f.id,
                                    "pdf",
                                    `${f.contract}_${f.month}_${f.year}`
                                  )
                                }
                              >
                                <FileText className="w-3.5 h-3.5 mr-2" />
                                Download PDF
                              </DropdownMenuItem>
                            )}
                            {hasPpt && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDownload(
                                    f.id,
                                    "pptx",
                                    `${f.contract}_${f.month}_${f.year}`
                                  )
                                }
                              >
                                <Presentation className="w-3.5 h-3.5 mr-2" />
                                Download PPT
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </>
                  )}

                  {hasPermission("delete_finalized_boarpacks") && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive/30 hover:bg-destructive/5 flex-shrink-0"
                      onClick={() => onDeleteClick(f.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        contractName={contractName}
        onUploadSuccess={() => {
          setImportOpen(false);
          onImportSuccess();
        }}
      />

      <FinalizeConfirmDialog
        open={!!pendingFinalize}
        onCancel={() => setPendingFinalize(null)}
        onConfirm={() => {
          handleFinalizeToggle(pendingFinalize.f, pendingFinalize.newValue);
          setPendingFinalize(null);
        }}
      />

      {viewer && (
        <DocViewerModal
          open={!!viewer}
          onClose={() => setViewer(null)}
          doc={viewer.doc}
          title={viewer.title}
          displayType={viewer.displayType}
        />
      )}

      {downloadDialog && !downloadDialog.minimized && (
        <Dialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setDownloadDialog((prev) => (prev ? { ...prev, minimized: true } : null));
          }}
        >
          <DialogContent className="w-[95vw] sm:max-w-sm" showCloseButton={false}>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between pr-1">
                <span>Downloading File</span>
                <button
                  onClick={() => setDownloadDialog((prev) => ({ ...prev, minimized: true }))}
                  className=" rounded hover:bg-muted text-muted-foreground"
                >
                  <Minus className="w-4 h-4" />
                </button>
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-10 h-10 rounded-full border-4 border-green-200 border-t-green-600 animate-spin" />
              <div className="text-center min-w-0 max-w-full">
                <p className="text-sm font-medium break-all">
                  {downloadDialog.name}.{downloadDialog.type}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Please wait while your file is being prepared…
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {downloadDialog?.minimized && (
        <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-4 py-2.5 bg-white border border-border rounded-full shadow-lg">
          <div className="w-3.5 h-3.5 rounded-full border-2 border-green-200 border-t-green-600 animate-spin flex-shrink-0" />
          <span className="text-sm font-medium text-foreground">Downloading…</span>
          <button
            onClick={() => setDownloadDialog((prev) => ({ ...prev, minimized: false }))}
            className="ml-1 p-0.5 rounded hover:bg-muted text-muted-foreground"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
const FinalizedBoardPack = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const hasFetched = useRef(false);

  const fetchReports = useCallback(async () => {
    try {
      const res = await apiCall("get", "/report/get-all-reports", null, null, false, false);
      if (res?.success && Array.isArray(res?.data)) setReports(res.data);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchReports();
  }, []);

  const handleDelete = async () => {
    if (!confirmId) return;
    try {
      await apiCall("delete", `/report/delete-report?id=${confirmId}`, null, null, false, true);
      setConfirmId(null);
      fetchReports();
    } catch (err) {
      console.error("Delete failed:", err);
      setConfirmId(null);
    }
  };

  if (selectedContract && selectedYear) {
    return (
      <>
        <FilesListView
          contractName={selectedContract}
          year={selectedYear}
          reports={reports}
          onBackToYears={() => setSelectedYear(null)}
          onBackToRoot={() => {
            setSelectedContract(null);
            setSelectedYear(null);
          }}
          onImportSuccess={fetchReports}
          onDeleteClick={(id) => setConfirmId(id)}
        />
        <DeleteConfirmDialog
          open={!!confirmId}
          onCancel={() => setConfirmId(null)}
          onConfirm={handleDelete}
        />
      </>
    );
  }

  if (selectedContract) {
    return (
      <YearListView
        contractName={selectedContract}
        reports={reports}
        onSelectYear={(year) => setSelectedYear(year)}
        onBackToRoot={() => setSelectedContract(null)}
        onImportSuccess={fetchReports}
      />
    );
  }

  return (
    <>
      <ContractListView
        reports={reports}
        loading={loading}
        onSelectContract={(name) => setSelectedContract(name)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onImportSuccess={fetchReports}
      />
      <DeleteConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default FinalizedBoardPack;